import { httpClient } from '@shared/api';
import type {
  Broadcast,
  BroadcastCreateRequest,
  BroadcastListResponse,
  BroadcastStats,
  BroadcastUpdateRequest,
  RecipientsPreview,
} from '../model/types';

/**
 * API рассылок (роутер broadcasts, N-3 + N-4).
 * Все эндпоинты под /assistants/{assistantId}/broadcasts/...
 */
export const broadcastApi = {
  async list(assistantId: string): Promise<Broadcast[]> {
    const { data } = await httpClient.get<BroadcastListResponse>(
      `/assistants/${assistantId}/broadcasts`,
    );
    return data.items;
  },

  async get(assistantId: string, broadcastId: string): Promise<Broadcast> {
    const { data } = await httpClient.get<Broadcast>(
      `/assistants/${assistantId}/broadcasts/${broadcastId}`,
    );
    return data;
  },

  async create(assistantId: string, payload: BroadcastCreateRequest): Promise<Broadcast> {
    const { data } = await httpClient.post<Broadcast>(
      `/assistants/${assistantId}/broadcasts`,
      payload,
    );
    return data;
  },

  async update(
    assistantId: string,
    broadcastId: string,
    payload: BroadcastUpdateRequest,
  ): Promise<Broadcast> {
    const { data } = await httpClient.patch<Broadcast>(
      `/assistants/${assistantId}/broadcasts/${broadcastId}`,
      payload,
    );
    return data;
  },

  async remove(assistantId: string, broadcastId: string): Promise<void> {
    await httpClient.delete(`/assistants/${assistantId}/broadcasts/${broadcastId}`);
  },

  async uploadMedia(assistantId: string, broadcastId: string, file: File): Promise<Broadcast> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await httpClient.post<Broadcast>(
      `/assistants/${assistantId}/broadcasts/${broadcastId}/media`,
      form,
      { headers: { 'Content-Type': undefined } },
    );
    return data;
  },

  async deleteMedia(assistantId: string, broadcastId: string, index: number): Promise<Broadcast> {
    const { data } = await httpClient.delete<Broadcast>(
      `/assistants/${assistantId}/broadcasts/${broadcastId}/media/${index}`,
    );
    return data;
  },

  async previewRecipients(assistantId: string, broadcastId: string): Promise<RecipientsPreview> {
    const { data } = await httpClient.get<RecipientsPreview>(
      `/assistants/${assistantId}/broadcasts/${broadcastId}/recipients/preview`,
    );
    return data;
  },

  /** POST .../send — запустить рассылку (снимок + очередь). 409 если не draft. */
  async send(assistantId: string, broadcastId: string): Promise<Broadcast> {
    const { data } = await httpClient.post<Broadcast>(
      `/assistants/${assistantId}/broadcasts/${broadcastId}/send`,
    );
    return data;
  },

  /** GET .../stats — статистика доставки (разбивка по статусам + причины). */
  async getStats(assistantId: string, broadcastId: string): Promise<BroadcastStats> {
    const { data } = await httpClient.get<BroadcastStats>(
      `/assistants/${assistantId}/broadcasts/${broadcastId}/stats`,
    );
    return data;
  },

  /** POST .../cancel — стоп-кран. 409 если не queued/sending. */
  async cancel(assistantId: string, broadcastId: string): Promise<Broadcast> {
    const { data } = await httpClient.post<Broadcast>(
      `/assistants/${assistantId}/broadcasts/${broadcastId}/cancel`,
    );
    return data;
  },
};
